import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Users, UserCheck, UserX, Clock, Building2, Fingerprint, Umbrella, Coffee, RefreshCw, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { formatPunchTime } from '../lib/timeFormat';
import Sparkline from '../components/Sparkline';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [breakOvertimes, setBreakOvertimes] = useState([]);
  const [trends, setTrends] = useState(null);
  const [trendDays, setTrendDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [, setNowTick] = useState(0);

  useEffect(() => {
    fetchAll();
  }, []);

  // Refetch trends when period changes
  useEffect(() => {
    if (!loading) {
      fetchTrendsOnly();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendDays]);

  // Tick every second so "MAJ il y a Xs" updates
  useEffect(() => {
    const t = setInterval(() => setNowTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-refresh every 30s when tab is visible
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchAllSilent();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchAll = async () => {
    try {
      const [s, b, t] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/break-overtimes-today'),
        api.get('/dashboard/trends-7d', { params: { days: trendDays } })
      ]);
      setStats(s.data);
      setBreakOvertimes(b.data?.overtimes || []);
      setTrends(t.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load dashboard', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSilent = async () => {
    try {
      const [s, b, t] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/break-overtimes-today'),
        api.get('/dashboard/trends-7d', { params: { days: trendDays } })
      ]);
      setStats(s.data);
      setBreakOvertimes(b.data?.overtimes || []);
      setTrends(t.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.warn('auto-refresh failed', error);
    }
  };

  const fetchTrendsOnly = async () => {
    try {
      const t = await api.get('/dashboard/trends-7d', { params: { days: trendDays } });
      setTrends(t.data);
    } catch (error) {
      console.warn('trends fetch failed', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  const attendanceData = [
    { name: 'Présents', value: stats?.present_today || 0, color: '#059669' },
    { name: 'Absents', value: stats?.absent_today || 0, color: '#E11D48' },
    { name: 'Retards', value: stats?.late_today || 0, color: '#D97706' }
  ];

  const secondsSinceRefresh = Math.max(0, Math.floor((Date.now() - lastRefresh.getTime()) / 1000));

  const periodLabel = trendDays === 7 ? '7j' : trendDays === 14 ? '14j' : '30j';
  const vsLabel = trendDays === 7 ? 'vs hier' : `sur ${trendDays}j`;

  const renderTrendBadge = (series) => {
    if (!series || series.length < 2) return null;
    const last = series[series.length - 1]?.count ?? 0;
    const prev = trendDays === 7
      ? series[series.length - 2]?.count ?? 0
      : Math.round(series.slice(0, -1).reduce((s, d) => s + d.count, 0) / Math.max(1, series.length - 1));
    if (last === prev) {
      return <span className="text-xs text-slate-400" title="Stable">—</span>;
    }
    const delta = last - prev;
    const up = delta > 0;
    return (
      <span
        className={`text-xs font-medium ${up ? 'text-red-600' : 'text-green-600'}`}
        title={up ? `+${delta} vs moyenne` : `${delta} vs moyenne`}
      >
        {up ? '▲' : '▼'} {Math.abs(delta)}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Tableau de Bord</h1>
          <p className="text-slate-600 mt-1">Bienvenue, {user?.full_name}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-1 bg-white" data-testid="trend-period-selector">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setTrendDays(d)}
                className={`px-3 py-1 text-xs rounded font-medium transition ${
                  trendDays === d
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`period-${d}`}
              >
                {d}j
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setAutoRefresh(v => !v)}
            className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900 transition"
            data-testid="toggle-dashboard-auto-refresh"
            title="Activer/désactiver le rafraîchissement automatique"
          >
            <span className={`inline-block w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
            <span className="tabular-nums">
              {autoRefresh ? 'Temps réel' : 'Pause'}
              {' • '}
              <span data-testid="dashboard-last-refresh">MAJ il y a {secondsSinceRefresh}s</span>
            </span>
          </button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllSilent}
            data-testid="dashboard-refresh-btn"
            title="Rafraîchir maintenant"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-all">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Employés</CardTitle>
            <Users className="w-5 h-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{stats?.total_employees || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-all">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Présents Aujourd'hui</CardTitle>
            <UserCheck className="w-5 h-5 text-success" />
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between gap-2">
              <div>
                <div className="text-3xl font-bold text-success">{stats?.present_today || 0}</div>
                {trends?.present && (
                  <div className="mt-1 flex items-center gap-1" data-testid="trend-present">
                    {/* For present, up is good → green arrow */}
                    {(() => {
                      const s = trends.present;
                      if (s.length < 2) return null;
                      const last = s[s.length - 1].count;
                      const prev = trendDays === 7
                        ? s[s.length - 2].count
                        : Math.round(s.slice(0, -1).reduce((sum, d) => sum + d.count, 0) / Math.max(1, s.length - 1));
                      if (last === prev) return <span className="text-xs text-slate-400">—</span>;
                      const up = last > prev;
                      return (
                        <span className={`text-xs font-medium ${up ? 'text-green-600' : 'text-amber-600'}`}>
                          {up ? '▲' : '▼'} {Math.abs(last - prev)}
                        </span>
                      );
                    })()}
                    <span className="text-xs text-slate-400">{vsLabel}</span>
                  </div>
                )}
              </div>
              {trends?.present && (
                <Sparkline data={trends.present} color="#059669" width={90} height={28} />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-all">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Absents Aujourd'hui</CardTitle>
            <UserX className="w-5 h-5 text-error" />
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between gap-2">
              <div>
                <div className="text-3xl font-bold text-error">{stats?.absent_today || 0}</div>
                {trends?.absent && (
                  <div className="mt-1 flex items-center gap-1" data-testid="trend-absent">
                    {renderTrendBadge(trends.absent)}
                    <span className="text-xs text-slate-400">{vsLabel}</span>
                  </div>
                )}
              </div>
              {trends?.absent && (
                <Sparkline data={trends.absent} color="#E11D48" width={90} height={28} />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-all">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Retards Aujourd'hui</CardTitle>
            <Clock className="w-5 h-5 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between gap-2">
              <div>
                <div className="text-3xl font-bold text-warning">{stats?.late_today || 0}</div>
                {trends?.late && (
                  <div className="mt-1 flex items-center gap-1" data-testid="trend-late">
                    {renderTrendBadge(trends.late)}
                    <span className="text-xs text-slate-400">{vsLabel}</span>
                  </div>
                )}
              </div>
              {trends?.late && (
                <Sparkline data={trends.late} color="#D97706" width={90} height={28} />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-all">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Congés En Attente</CardTitle>
            <Umbrella className="w-5 h-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{stats?.pending_leaves || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Break Overtime Widget */}
      <Card
        className={`border shadow-sm ${breakOvertimes.length > 0 ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200'}`}
        data-testid="break-overtime-widget"
      >
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Coffee className="w-5 h-5 text-amber-600" />
                Dépassements de pause du jour
                {breakOvertimes.length > 0 && (
                  <Badge variant="outline" className="bg-amber-100 text-amber-900 border-amber-300" data-testid="break-overtime-count">
                    {breakOvertimes.length}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Employés qui ont dépassé le créneau de pause prévu par leur poste
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {breakOvertimes.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500 flex flex-col items-center gap-2" data-testid="break-overtime-empty">
              <UserCheck className="w-10 h-10 text-green-500" />
              Aucun dépassement de pause aujourd'hui
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {breakOvertimes.map((ov) => {
                const severity = ov.break_overtime_minutes > 30 ? 'high' : ov.break_overtime_minutes > 15 ? 'medium' : 'low';
                const colorMap = {
                  high:   { bg: 'bg-red-50',    border: 'border-red-200',    badge: 'bg-red-100 text-red-800 border-red-300' },
                  medium: { bg: 'bg-amber-50',  border: 'border-amber-200',  badge: 'bg-amber-100 text-amber-800 border-amber-300' },
                  low:    { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-800 border-yellow-300' }
                };
                const c = colorMap[severity];
                return (
                  <button
                    key={ov.employee_id}
                    type="button"
                    onClick={() => navigate(`/attendance?employee_id=${ov.employee_id}`)}
                    className={`w-full text-left flex items-center justify-between gap-3 border ${c.border} ${c.bg} rounded-lg p-3 hover:shadow-md hover:border-slate-400 transition cursor-pointer`}
                    data-testid={`break-overtime-row-${ov.employee_id}`}
                    title="Voir l'historique des présences de cet employé"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900 text-sm">{ov.full_name}</p>
                        {ov.position && (
                          <Badge variant="outline" className="text-xs bg-white text-slate-600">
                            {ov.position}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        Pause : {formatPunchTime(ov.break_start)} → {formatPunchTime(ov.break_end)}
                        <span className="ml-2 text-slate-400">({ov.break_duration_minutes} min au total)</span>
                      </p>
                    </div>
                    <Badge variant="outline" className={`${c.badge} font-mono shrink-0`}>
                      {severity === 'high' && <AlertTriangle className="w-3 h-3 mr-1" />}
                      +{ov.break_overtime_minutes} min
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {user?.role === 'super_admin' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Total Entreprises
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-slate-900">{stats?.total_companies || 0}</div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Fingerprint className="w-5 h-5 text-primary" />
                Total Pointeurs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-slate-900">{stats?.total_devices || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Répartition Présence du Jour</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={attendanceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {attendanceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Statistiques Présence</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={attendanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#64748B" />
                <YAxis stroke="#64748B" />
                <Tooltip />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {attendanceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
